import React, { useState } from 'react';
import { ArrowRight, KeyRound, Mail } from 'lucide-react';
import type { Session } from '@supabase/supabase-js';
import { sendLoginCode, verifyLoginCode, isNetworkError } from '../lib/talentPlatform';

const OFFLINE_MSG = 'Our login service is temporarily unreachable. Please try again in a few minutes, or email partners@voqals.com and we will sort it out.';

/* ──────────────────────────────────────────────────────────────
   TalentAuth — passwordless email login card shared by the
   /voice-talent application gate and the talent portal.
   Flow: email → 6-digit code (or the magic link in the same
   email, which signs the visitor in via redirect).
   ────────────────────────────────────────────────────────────── */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function TalentAuth({ onAuthed, title, subtitle }: {
  onAuthed: (session: Session) => void;
  title?: string;
  subtitle?: string;
}) {
  const [stage, setStage] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [resent, setResent] = useState(false);

  const emailValid = EMAIL_RE.test(email);

  const sendCode = async () => {
    if (!emailValid || busy) return;
    setBusy(true);
    setError('');
    try {
      await sendLoginCode(email.trim());
      setStage('code');
      setResent(false);
    } catch (err: any) {
      setError(
        isNetworkError(err) ? OFFLINE_MSG
        : err?.message?.includes('rate') ? 'Too many attempts — please wait a minute and try again.'
        : 'Could not send the code. Check the email address and try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (code.trim().length < 6 || busy) return;
    setBusy(true);
    setError('');
    try {
      const session = await verifyLoginCode(email.trim(), code.trim());
      onAuthed(session);
    } catch (err) {
      setError(isNetworkError(err) ? OFFLINE_MSG : "That code didn't match — double-check it, or tap resend.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2 text-[#56CFFC] text-[10px] font-bold tracking-[0.2em] uppercase">
          <KeyRound size={13} /> {title || 'Your Talent Account'}
        </div>
        <p className="text-white/70 text-[12px] leading-relaxed">
          {subtitle || 'One account for your profile, your voice sample, and every casting call. No password — we email you a login code.'}
        </p>
      </div>

      {stage === 'email' ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <label className="text-[9px] uppercase tracking-widest text-white/80 font-medium">Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') sendCode(); }}
              placeholder="you@email.com"
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl py-2.5 px-4 text-white text-sm focus:outline-none focus:border-[#56CFFC]/50 transition-colors placeholder:text-gray-600"
            />
          </div>
          <button
            type="button"
            onClick={sendCode}
            disabled={!emailValid || busy}
            className="group relative w-full py-2.5 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full text-[10px] font-bold tracking-[0.2em] uppercase overflow-hidden transition-all hover:shadow-[0_0_30px_rgba(86,207,252,0.3)] disabled:opacity-35 flex items-center justify-center gap-2"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
            <span className="relative z-10 flex items-center gap-2">
              {busy ? 'Sending…' : 'Email Me A Code'} {!busy && <Mail size={13} />}
            </span>
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-white/75 text-[12px] leading-relaxed">
            We sent a code to <span className="text-white font-semibold">{email}</span>. Enter it below —
            or just click the login link inside that email.
          </p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 10))}
            onKeyDown={e => { if (e.key === 'Enter') verify(); }}
            placeholder="Enter code"
            className="w-full bg-white/[0.03] border border-white/10 rounded-xl py-3 px-4 text-white text-xl tracking-[0.4em] text-center font-mono focus:outline-none focus:border-[#56CFFC]/50 transition-colors placeholder:text-gray-600 placeholder:text-sm placeholder:tracking-normal placeholder:font-sans"
          />
          <button
            type="button"
            onClick={verify}
            disabled={code.trim().length < 6 || busy}
            className="group relative w-full py-2.5 bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white rounded-full text-[10px] font-bold tracking-[0.2em] uppercase overflow-hidden transition-all hover:shadow-[0_0_30px_rgba(86,207,252,0.3)] disabled:opacity-35 flex items-center justify-center gap-2"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" />
            <span className="relative z-10 flex items-center gap-2">
              {busy ? 'Verifying…' : 'Verify & Continue'} {!busy && <ArrowRight size={13} />}
            </span>
          </button>
          <div className="flex items-center justify-between text-[11px]">
            <button type="button" onClick={() => { setStage('email'); setCode(''); setError(''); }} className="text-white/55 hover:text-white transition-colors">
              Change email
            </button>
            <button
              type="button"
              onClick={async () => { await sendCode(); setResent(true); }}
              className="text-[#56CFFC]/80 hover:text-[#56CFFC] transition-colors"
            >
              {resent ? 'Code re-sent ✓' : 'Resend code'}
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-amber-300/90 text-[11px] leading-snug">{error}</p>}
    </div>
  );
}
